import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { apiGet } from '../api/client.js';

export interface SyncRunSummary {
  id: string;
  storeId: string;
  kind: 'listings' | 'orders' | 'full';
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'success' | 'partial' | 'failed';
  recordsSeen: number;
  recordsUpserted: number;
  errorText: string | null;
}

export interface StoreSummary {
  id: string;
  storeName: string;
  ebayUserId: string;
  marketplaceId: string;
  connectedAt: string;
  lastSyncRunAt: string | null;
  recentSyncRuns: SyncRunSummary[];
}

interface StoreState {
  stores: StoreSummary[];
  loading: boolean;
  selectedStoreId: string | 'all';
  setSelected(id: string | 'all'): void;
  refresh(): void;
}

const Ctx = createContext<StoreState | null>(null);

export function StoreProvider({ children }: { children: ReactNode }): JSX.Element {
  const [stores, setStores] = useState<StoreSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | 'all'>(
    () => localStorage.getItem('selectedStoreId') ?? 'all',
  );

  const refresh = (): void => {
    setLoading(true);
    apiGet<{ stores: StoreSummary[] }>('/api/stores')
      .then((r) => setStores(r.stores))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    localStorage.setItem('selectedStoreId', selected);
  }, [selected]);

  return (
    <Ctx.Provider value={{ stores, loading, selectedStoreId: selected, setSelected, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useStores(): StoreState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStores outside provider');
  return v;
}
